import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import FileUploadField from '@wix/da-shared-react/pkg/FileUploadField';
import type { UploadAttachmentSuccessCallback } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import {
  MIME_CONFIG,
  mimeTypeToPcpType,
} from '@wix/da-shared-react/pkg/utils/mime';
import { removeAttachment } from '@wix/da-shared-react/pkg/redux/uploads/actions';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { SubmitFormContext } from '../../context';
import { useSubmitField } from '../../hooks/useSubmitField';
import CoreUpsellNote from '../../layouts/common/CoreUpsellNote';
import IsExclusiveNote from './_partials/IsExclusiveNote';
import PcpLabelWithToggleField from '../PcpLabelWithToggleField';
import {
  initAdapter,
  originalAttachedInitAdapter,
  useSubmitAdapter,
} from './adapters';
import { useValidator } from './validation';
import { getFileExtension, shouldRejectFile } from './utils';
import { updatePcpAttachments } from '../../redux/actions';

interface Props {
  className?: string;
  deviation?: PapiDeviation;
  inTier?: boolean;
  hasCoreMembership?: boolean;
  allowUpload?: boolean;
  /**
   * By default, the PCP fields only send their data on submit after they've been altered by the user.
   *
   * Toggling this prop allows us to bypass this isDirty check and submit with the PCP data regardless. Useful in
   * smaller, customized submit flows.
   */
  allowCleanFieldSubmit?: boolean;
}

const FIELD_NAME = 'attachments';

const AttachmentsField: React.FC<Props> = ({
  className,
  deviation,
  inTier,
  allowCleanFieldSubmit,
  hasCoreMembership = false,
  allowUpload,
}) => {
  const { t: translateFn } = useTranslation();
  const t = (key: string, ...args: any[]) =>
    (translateFn as (key: string, ...args: any[]) => ReturnType<typeof translateFn>)(
      `deviationSubmit.form.attachments.${key}`,
      ...args
    );

  const [showSourceRemoveConfirmation, setShowSourceRemoveConfirmation] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const validator = useValidator();
  const { watch, trigger, errors, setValue, getValues } = useFormContext();
  const submitFormContext = useContext(SubmitFormContext);
  const initAssets = submitFormContext?.initResponse?.pcp?.assets || [];
  const showAttachments = watch('showAttachments');
  const errorMessage: string | undefined = errors.showAttachments?.message;

  const submitAdapter = useSubmitAdapter(allowCleanFieldSubmit);
  const {
    id,
    field: { value: attachments = [], onChange },
  } = useSubmitField(FIELD_NAME, {
    initAdapter,
    submitAdapter,
    submitIfCleanOnEdit: ['attachments', 'originalAttached'],
    validator: (ctx, tr) => validator(tr),
  });

  const {
    field: { onChange: onOriginalToggle, value: originalAttached },
  } = useSubmitField('originalAttached', {
    initAdapter: originalAttachedInitAdapter,
  });

  useEffect(
    () => {
      const { initResponse, isEdit, isDraft, params } = submitFormContext || {};
      if (initResponse && params) {
        setValue(
          FIELD_NAME,
          initAdapter(initResponse, { ...params, isEdit, isDraft })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initAssets.length]
  );

  // Exclusives can't be sold as PCP. Non-core can edit deviations which have PCP already.
  const isAdoptable = deviation?.isAdoptable;
  const eligibleForAttachments =
    !isAdoptable &&
    (hasCoreMembership || attachments.length > 0 || originalAttached);

  const onDrop = useCallback(
    (files, e): boolean => {
      const rejectedFile = files.find(shouldRejectFile);
      if (rejectedFile) {
        dispatch(
          addErrorNotification(
            translateFn(
              'deviationSubmit.validation.error.attachments.unsupported',
              {
                extension: getFileExtension(rejectedFile),
              }
            )
          )
        );
        // reject files
        return false;
      }
      // accept files
      return true;
    },
    [translateFn, dispatch]
  );

  const handleUploadSuccess: UploadAttachmentSuccessCallback = useCallback(
    ({ deviationId, fileId, file: { name: fileName, size, type } }) => {
      const currentValue = getValues(FIELD_NAME);
      const newValue = [
        ...currentValue,
        {
          deviationId,
          fileId,
          name: fileName,
          size,
          type: mimeTypeToPcpType(type),
        },
      ];
      onChange(newValue);
      dispatch(
        updatePcpAttachments([
          {
            deviationId,
            fileId,
            filename: fileName,
            filesize: size,
            type,
            isOriginal: false,
          },
        ])
      );
      setValue('isPurchasable', newValue.length > 0);
      trigger('showAttachments');
    },
    [onChange, getValues, setValue, trigger]
  );

  const handleUploadFailure = useCallback(
    (_, error: Error) => {
      dispatch(
        addErrorNotification(
          translateFn('deviationSubmit.validation.error.attachments.server', {
            error: error.message,
          })
        )
      );
    },
    [dispatch, translateFn]
  );

  const handleRemove = useCallback(
    ({ deviationId, fileId }) => {
      if (deviationId === 0) {
        setShowSourceRemoveConfirmation(true);
      } else {
        // mark attachment as busy because removal is async
        let currentValue = getValues(FIELD_NAME);
        setValue(
          FIELD_NAME,
          currentValue.map(item =>
            item.fileId === fileId ? { ...item, status: 'busy' } : item
          ),
          { shouldDirty: false, shouldValidate: false }
        );

        // call remove api and update fields on success
        dispatch(
          removeAttachment(fileId, deviation!.deviationId, 'pcp', () => {
            currentValue = getValues(FIELD_NAME);
            const newValue = [
              ...currentValue.filter(item => item.fileId !== fileId),
            ];
            onChange(newValue);
            setValue('isPurchasable', newValue.length > 0);
            trigger('showAttachments');
          })
        );
      }
    },
    [
      onChange,
      getValues,
      setValue,
      trigger,
      setShowSourceRemoveConfirmation,
      dispatch,
      deviation,
    ]
  );

  const handleRemoveSource = useCallback(() => {
    setShowSourceRemoveConfirmation(false);
    onOriginalToggle(false);

    const currentValue = getValues(FIELD_NAME);
    const newValue = [...currentValue.filter(item => item.deviationId !== 0)];
    onChange(newValue);
    setValue('isPurchasable', newValue.length > 0);
    trigger('originalAttached');
    trigger('showAttachments');
  }, [
    setShowSourceRemoveConfirmation,
    getValues,
    setValue,
    onChange,
    onOriginalToggle,
    trigger,
  ]);

  const handleRemoveSourceCancel = useCallback(() => {
    setShowSourceRemoveConfirmation(false);
  }, [setShowSourceRemoveConfirmation]);

  return (
    <div className={className}>
      <PcpLabelWithToggleField deviation={deviation!} inTier={inTier} />
      {showAttachments && !eligibleForAttachments && isAdoptable && (
        <IsExclusiveNote />
      )}
      {showAttachments && !eligibleForAttachments && !hasCoreMembership && (
        <CoreUpsellNote label={t('upsell.label')} ctaLabel={t('upsell.cta')} />
      )}
      {showAttachments && eligibleForAttachments && (
        <FileUploadField
          id={id}
          uploadType={UploadType.ATTACHMENT}
          uploadParams={{
            parentDeviationId: deviation?.deviationId,
            attachmentType: 'pcp',
          }}
          items={attachments}
          acceptedMimeTypes={MIME_CONFIG.forDeviationAttachment}
          allowUpload={allowUpload}
          uploadButtonInList
          uploadButtonLabel={t('upload.label')}
          onSuccess={handleUploadSuccess}
          onFailure={handleUploadFailure}
          onRemove={handleRemove}
          onDrop={onDrop}
          customFirstFileName={t('item.original')}
        />
      )}
      {eligibleForAttachments && errorMessage && (
        <InputErrorMessage>{errorMessage}</InputErrorMessage>
      )}
      {showSourceRemoveConfirmation && (
        <ConfirmationModal
          isOpen
          title={t('removeSource.title')}
          text={t('removeSource.text.pcp_multi_deviations')}
          confirmBtnType="danger"
          confirmBtnLabel={t('removeSource.confirmCta')}
          cancelBtnLabel={t('removeSource.cancelCta')}
          onCancel={handleRemoveSourceCancel}
          onConfirm={handleRemoveSource}
        />
      )}
    </div>
  );
};
AttachmentsField.displayName = 'AttachmentsField';

export default AttachmentsField;
